export const DEFAULT_PAGE_SIZE = 20;

export function getPaginationMeta(totalCount, page, pageSize = DEFAULT_PAGE_SIZE) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  return { totalPages, currentPage, pageSize, totalCount };
}

export function getVisiblePageNumbers(currentPage, totalPages) {
  return Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1
  );
}

export function buildListQueryParams(args = {}) {
  const params = {};

  Object.entries(args ?? {}).forEach(([key, value]) => {
    if (value == null || value === '') return;

    if (key === 'filter') {
      if (Array.isArray(value)) {
        const list = value.filter(Boolean);
        if (list.length) params.filter = list;
      } else {
        params.filter = value;
      }
      return;
    }

    params[key] = value;
  });

  return params;
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    if (value == null || value === '') continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function isPaginationBlock(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return (
    value.total_count != null ||
    value.totalCount != null ||
    value.total_pages != null ||
    value.totalPages != null ||
    value.per_page != null ||
    value.perPage != null ||
    value.current_page != null ||
    value.currentPage != null
  );
}

function findPaginationBlock(response) {
  if (!response || typeof response !== 'object') return {};

  const candidates = [
    response.pagination,
    response.data?.pagination,
    response.data?.data?.pagination,
    response.data?.meta?.pagination,
    response.meta?.pagination,
    response.data?.trackings?.pagination,
    response.data?.account_behaviour_trackings?.pagination,
    response.meta,
    response.data?.meta,
    response.data?.pagy,
    response.data,
    response,
  ];

  for (const candidate of candidates) {
    if (isPaginationBlock(candidate)) return candidate;
  }
  return {};
}

export function getServerPagination(response, { page, pageSize = DEFAULT_PAGE_SIZE } = {}) {
  const pagination = findPaginationBlock(response);

  const perPage =
    firstFiniteNumber(
      pagination.per_page,
      pagination.perPage,
      pagination.limit,
      pagination.limit_value,
      pageSize
    ) || pageSize;

  let totalCount = firstFiniteNumber(
    pagination.total_count,
    pagination.totalCount,
    pagination.total_entries,
    pagination.totalEntries,
    pagination.total,
    response?.total_count,
    response?.totalCount,
    response?.data?.total_count,
    response?.data?.total
  );

  const totalPages = firstFiniteNumber(
    pagination.total_pages,
    pagination.totalPages,
    pagination.total_page,
    pagination.last_page,
    pagination.lastPage
  );
  if ((totalCount == null || totalCount <= 0) && totalPages > 0) {
    totalCount = totalPages * perPage;
  }

  const requestedPage = firstFiniteNumber(page) || 1;

  return {
    currentPage: requestedPage,
    totalCount: totalCount != null && totalCount > 0 ? totalCount : 0,
    pageSize: perPage,
  };
}
